import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pmsKeys } from '../queryKeys';
import { runsApi } from './runs.api';
import { formsApi } from '../forms/forms.api';
import type {
  CloseRunResult,
  CreateRunPayload,
  EvaluationRun,
  PublishFormWithRunResult,
  RemindResult,
  RunParticipant,
} from '../types';

/** Read paths for M4+; the wizard itself only publishes. */
export function usePmsRuns(status?: string) {
  return useQuery({
    queryKey: pmsKeys.runs({ status }),
    queryFn: () => runsApi.list(status),
    select: (d) => (Array.isArray(d) ? d : []) as EvaluationRun[],
  });
}

export function usePmsRun(runId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.run(runId ?? ''),
    queryFn: () => runsApi.get(runId as string),
    enabled: !!runId,
  });
}

export function usePmsRunPreview(runId: string | undefined) {
  return useQuery({
    queryKey: pmsKeys.runPreview(runId ?? ''),
    queryFn: () => runsApi.preview(runId as string),
    enabled: !!runId,
  });
}

/**
 * Step 4's Publish — the ONLY way a run comes into existence.
 *
 * Four server calls, in this order and for these reasons:
 *
 *   1. `POST /forms/:id/publish` — the template must reach Published FIRST,
 *      because resolution's E3 check asks "does this designation have a
 *      Published template?" and would fail every participant otherwise.
 *   2. `POST /runs` — the run carries the name, window and behaviour overrides
 *      the step collected.
 *   3. `POST /runs/:id/resolve` — expands the audience and walks the reporting
 *      chain for each employee, recording exceptions.
 *   4. `POST /runs/:id/publish` — snapshots, tokens and invitations, atomically.
 *
 * Only step 4 is atomic, and it can only be made atomic server-side. If step 3
 * or 4 fails the template stays Published and the run stays Draft/Preview, which
 * is recoverable — so the error is surfaced with the run id rather than
 * swallowed, and nothing is rolled back behind the user's back.
 *
 * `exception_count > 0` is NOT a failure: FR-204 explicitly allows publishing
 * with exceptions, and the excluded employees are logged to T6.
 */
export function usePublishPmsFormWithRun() {
  const qc = useQueryClient();

  return useMutation<
    PublishFormWithRunResult,
    Error,
    { templateId: string; run: CreateRunPayload }
  >({
    mutationFn: async ({ templateId, run }) => {
      const template = await formsApi.publish(templateId);
      const created = await runsApi.create(run);
      const resolved = await runsApi.resolve(created.run_id);
      const published = await runsApi.publish(created.run_id);

      return {
        template,
        run: published.run,
        published_count: published.published_count,
        // How many were actually mailed. Differs from `published_count` when
        // someone has no email address in the directory — they hold a token that
        // was never sent anywhere, which is the case the summary must surface.
        invited_count: published.invited_count,
        exception_count: published.exception_count,
        exceptions: resolved.exceptions,
      };
    },
    onSuccess: (_r, { templateId }) => {
      qc.invalidateQueries({ queryKey: pmsKeys.forms() });
      qc.invalidateQueries({ queryKey: pmsKeys.form(templateId) });
      qc.invalidateQueries({ queryKey: pmsKeys.formGate(templateId) });
      // Publishing puts a new cycle's participants into every dashboard total.
      qc.invalidateQueries({ queryKey: pmsKeys.dashboardAll() });
      qc.invalidateQueries({ queryKey: pmsKeys.runs() });
    },
  });
}

// ─── Post-publish run management (§7) ────────────────────────────────────────
//
// Every hook below wraps an endpoint that has existed and been tested on the
// server since M3 but had no way to be called: `runs.query.ts` exported reads
// and publish only, so once a run went live the admin had no controls at all —
// no extension, no chaser, no close, no abort.

/**
 * Anything that touches a run changes its participants too, so all three caches
 * go together. Shared rather than repeated because a hook that invalidated only
 * `run` would leave the preview list showing states the server no longer holds.
 */
function useRunInvalidation() {
  const qc = useQueryClient();
  return (runId: string) => {
    qc.invalidateQueries({ queryKey: pmsKeys.runs() });
    qc.invalidateQueries({ queryKey: pmsKeys.run(runId) });
    qc.invalidateQueries({ queryKey: pmsKeys.runPreview(runId) });
    /**
     * The dashboard counts PARTICIPANT ROWS AND THEIR STATE, so adding, removing,
     * closing or cancelling changes what it reports.
     *
     * Applied to every caller of this helper rather than the four that strictly
     * qualify — a resend or a deadline extension does not move a number. Listing
     * the exceptions means keeping that list in step with the dashboard's own
     * aggregates forever, and the cost of being wrong is a stale dashboard, while
     * the cost of being broad is one refetch of one aggregate on an admin screen.
     */
    qc.invalidateQueries({ queryKey: pmsKeys.dashboardAll() });
  };
}

/**
 * FR-209 — close the cycle: keep everyone who has submitted, stop everyone who
 * has not. The returned `stopped_count` / `kept_count` are what the confirmation
 * should have shown before this ran, and are worth showing after it too.
 */
export function useClosePmsRun() {
  const invalidate = useRunInvalidation();
  return useMutation<CloseRunResult, Error, string>({
    mutationFn: (runId) => runsApi.close(runId),
    onSuccess: (_r, runId) => invalidate(runId),
  });
}

/**
 * Abort — the cycle is void and nothing in it counts, including work already
 * submitted. Deliberately NOT the same operation as close: this one is for a run
 * published with the wrong form, where twelve scores on a wrong instrument are
 * worse than none. Callers must gate it behind a typed confirmation.
 */
export function useCancelPmsRun() {
  const invalidate = useRunInvalidation();
  return useMutation<{ run: EvaluationRun }, Error, string>({
    mutationFn: (runId) => runsApi.cancel(runId),
    onSuccess: (_r, runId) => invalidate(runId),
  });
}

/**
 * FR-206 — move the deadline for the whole run. This is the answer to "some
 * people have not filled it in" far more often than stopping them is.
 */
export function useExtendPmsRunExpiry() {
  const invalidate = useRunInvalidation();
  return useMutation<
    { run: EvaluationRun; extended_count: number },
    Error,
    { runId: string; newExpiryAt: string; role?: 'Self' | 'Evaluator' }
  >({
    mutationFn: ({ runId, newExpiryAt, role }) =>
      runsApi.extendRunExpiry(runId, newExpiryAt, role),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/** FR-206 — extend one person, which also revives them if they had expired. */
export function useExtendPmsParticipantExpiry() {
  const invalidate = useRunInvalidation();
  return useMutation<
    RunParticipant,
    Error,
    { runId: string; participantId: string; newExpiryAt: string; role?: 'Self' | 'Evaluator' }
  >({
    mutationFn: ({ runId, participantId, newExpiryAt, role }) =>
      runsApi.extendParticipantExpiry(runId, participantId, newExpiryAt, role),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/**
 * FR-207 — remind everyone who still owes an answer.
 *
 * The server decides WHO from participant state; the caller only says which half
 * of the cycle. Passing a list from the client would nag people who finished
 * between the page loading and the button being pressed.
 */
export function useRemindPmsRun() {
  const invalidate = useRunInvalidation();
  return useMutation<
    RemindResult,
    Error,
    { runId: string; role?: 'Self' | 'Evaluator' | 'Both' }
  >({
    mutationFn: ({ runId, role }) => runsApi.remind(runId, role),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/**
 * Take one person out of a live cycle.
 *
 * Until this existed the only way to remove anyone post-publish was aborting the
 * whole run, so one wrongly-included person kept getting mail, sat in their
 * manager's queue all cycle, and stopped the run ever auto-closing.
 */
export function useRemovePmsParticipant() {
  const invalidate = useRunInvalidation();
  return useMutation<
    { participant: RunParticipant; run_auto_closed: boolean },
    Error,
    { runId: string; participantId: string; reason: string }
  >({
    mutationFn: ({ runId, participantId, reason }) =>
      runsApi.removeParticipant(runId, participantId, reason),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/** Re-send the invitation mail to one participant — the chaser. */
export function useResendPmsInvitation() {
  const invalidate = useRunInvalidation();
  return useMutation<RunParticipant, Error, { runId: string; participantId: string }>({
    mutationFn: ({ runId, participantId }) => runsApi.resendInvitation(runId, participantId),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/**
 * Add someone the resolution missed or who joined after publish. The server
 * gives them their DESIGNATION's existing snapshot, so a late joiner answers
 * exactly what their peers answered.
 */
export function useAddPmsRunParticipant() {
  const invalidate = useRunInvalidation();
  return useMutation<RunParticipant, Error, { runId: string; employeeId: string }>({
    mutationFn: ({ runId, employeeId }) => runsApi.addParticipant(runId, employeeId),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/** Set an evaluator the chain could not resolve (an E2 exception). */
export function useOverridePmsEvaluator() {
  const invalidate = useRunInvalidation();
  return useMutation<
    RunParticipant,
    Error,
    { runId: string; participantId: string; evaluatorEmployeeId: string; reason: string }
  >({
    mutationFn: ({ runId, participantId, evaluatorEmployeeId, reason }) =>
      runsApi.overrideEvaluator(runId, participantId, {
        evaluator_employee_id: evaluatorEmployeeId,
        reason,
      }),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}

/**
 * Swap the evaluator on a live participant. The server refuses once the RM has
 * started answering — their answers would be orphaned — so surface that 409
 * rather than disabling the control on a guess.
 */
export function useReassignPmsEvaluator() {
  const invalidate = useRunInvalidation();
  return useMutation<
    RunParticipant,
    Error,
    { runId: string; participantId: string; evaluatorEmployeeId: string; reason: string }
  >({
    mutationFn: ({ runId, participantId, evaluatorEmployeeId, reason }) =>
      runsApi.reassignEvaluator(runId, participantId, {
        evaluator_employee_id: evaluatorEmployeeId,
        reason,
      }),
    onSuccess: (_r, { runId }) => invalidate(runId),
  });
}
